'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  createHumanInterviewBooking,
  CreateHumanInterviewBookingRequest,
  HumanInterviewBookingResponse,
} from '@/api/humanInterviewBooking';

interface HumanInterviewBookingState {
  data: HumanInterviewBookingResponse | null;
  isLoading: boolean;
  error: string | null;
}

interface HumanInterviewBookingContextType {
  booking: HumanInterviewBookingState;
  createBooking: (data: CreateHumanInterviewBookingRequest) => Promise<HumanInterviewBookingResponse | null>;
}

const HumanInterviewBookingContext = createContext<HumanInterviewBookingContextType | undefined>(
  undefined
);

export function HumanInterviewBookingProvider({ children }: { children: React.ReactNode }) {
  const [booking, setBookingState] = useState<HumanInterviewBookingState>({
    data: null,
    isLoading: false,
    error: null,
  });
  const { toast } = useToast();

  const createBooking = useCallback(
    async (data: CreateHumanInterviewBookingRequest): Promise<HumanInterviewBookingResponse | null> => {
      setBookingState(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await createHumanInterviewBooking(data);
        setBookingState({
          data: response,
          isLoading: false,
          error: null,
        });
        toast({
          description: `Interview booking created successfully for ${response.candidates.length} candidate(s)`,
        });
        return response;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create interview booking';
        setBookingState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          description: errorMessage,
          variant: 'error',
        });
        return null;
      }
    },
    [toast]
  );

  return (
    <HumanInterviewBookingContext.Provider
      value={{
        booking,
        createBooking,
      }}
    >
      {children}
    </HumanInterviewBookingContext.Provider>
  );
}

export function useHumanInterviewBooking() {
  const context = useContext(HumanInterviewBookingContext);
  if (context === undefined) {
    throw new Error('useHumanInterviewBooking must be used within a HumanInterviewBookingProvider');
  }
  return context;
}
